import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Connects to data-controller="post-harvest--photo-upload"
// Standalone photo/file upload controller using DirectUpload.
// Dispatches "post-harvest--photo-upload:changed" so parent controllers can react.
export default class extends Controller {
  static targets = [
    "preview",
    "image",
    "inputWrapper",
    "status",
    "signedId",
    "filePreview",
  ];
  static values = { fieldName: String };

  upload(event) {
    const file = event.target.files[0];
    if (!file) return;

    const url = document.querySelector(
      "meta[name='direct-upload-url']",
    )?.content;
    if (!url) return;

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "...";
      this.statusTarget.classList.remove("hidden");
    }

    const upload = new DirectUpload(file, url);
    upload.create((error, blob) => {
      if (error) {
        if (this.hasStatusTarget) this.statusTarget.textContent = "Error";
        return;
      }

      this.signedIdTarget.value = blob.signed_id;
      if (this.hasStatusTarget) this.statusTarget.classList.add("hidden");

      const isImage = file.type.startsWith("image/");

      if (isImage) {
        if (this.hasImageTarget) {
          this.#revokePreviewUrl();
          this._previewUrl = URL.createObjectURL(file);
          this.imageTarget.src = this._previewUrl;
          this.imageTarget.classList.remove("hidden");
        }
        if (this.hasFilePreviewTarget) {
          this.filePreviewTarget.classList.add("hidden");
        }
      } else {
        if (this.hasFilePreviewTarget) {
          this.filePreviewTarget.classList.remove("hidden");
          const nameEl = this.filePreviewTarget.querySelector("span");
          if (nameEl) nameEl.textContent = file.name;
        } else if (this.hasImageTarget) {
          // Fallback: hide image, show filename in preview
          this.imageTarget.classList.add("hidden");
        }
        if (this.hasImageTarget && !isImage) {
          this.imageTarget.classList.add("hidden");
        }
      }

      if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden");
      if (this.hasInputWrapperTarget)
        this.inputWrapperTarget.classList.add("hidden");

      this.dispatch("changed", {
        detail: {
          fieldName: this.fieldNameValue,
          signedId: blob.signed_id,
          filename: file.name,
          contentType: file.type,
        },
      });
    });
  }

  remove() {
    this.signedIdTarget.value = "";
    this.#revokePreviewUrl();
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden");
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.remove("hidden");
      const fileInput =
        this.inputWrapperTarget.querySelector("input[type='file']");
      if (fileInput) fileInput.value = "";
    }

    if (this.hasFilePreviewTarget) {
      this.filePreviewTarget.classList.add("hidden");
    }
    if (this.hasImageTarget) {
      this.imageTarget.src = "";
      this.imageTarget.classList.remove("hidden");
    }

    this.dispatch("changed", {
      detail: {
        fieldName: this.fieldNameValue,
        signedId: "",
        filename: "",
        contentType: "",
      },
    });
  }

  disconnect() {
    this.#revokePreviewUrl();
  }

  #revokePreviewUrl() {
    if (this._previewUrl) {
      URL.revokeObjectURL(this._previewUrl);
      this._previewUrl = null;
    }
  }
}
